import type { Deep7State } from './types';
import { civilFromEpochDay, epochDayFromCivil } from './calendar';
import { held } from './held';
import { KG_PER_POUND } from './constants';

/**
 * The Deep 7 bottomfish (M192): the six snappers and the grouper of the main Hawaiian Islands' deep slopes, fished
 * by hook and line at 100 to 400 m, held as NOAA's stock assessment holds them, one complex: "Benchmark Stock
 * Assessment for the Main Hawaiian Islands Deep 7 Bottomfish Complex in 2024 with Catch Projections Through 2029"
 * (Syslo, Oshima, Ma, Ducharme-Barth, Nadon and Carvalho 2024, NOAA Technical Memorandum NMFS-PIFSC-157,
 * doi:10.25923/5ssg-8d54; CC0 Public Domain, the NOAA Institutional Repository's record).
 *
 * **Its model is the assessment's**: JABBA's Pella–Tomlinson surplus production, the exploitable biomass at the start
 * of fishing year t (1 July to 30 June, "by fishing year (July 1–June 30)", Table 7, p. 79) "depended only on the
 * previous time period's exploitable biomass (Bt-1) and total catch (Ct-1)":
 *
 *   B_t = (B_t−1 + r/(m−1) · B_t−1 · (1 − (B_t−1 / K)^(m−1)) − C_t−1) · e^η_t     (Equations 5 and 11, pp. 25 and 26)
 *
 * "where ηt were identically distributed normal random variables with mean 0 and constant process error variance"
 * (p. 26): no bias correction, so a year's draw keeps the median. Its posterior medians: r 0.090, K 20.01 million lb,
 * m 0.800, σ² 0.005 (Table 12, p. 85). Each fishing year's change is spread over its days alike, a 365th (a 366th) a
 * day, so the next 1 July, before the year's draw, reaches the equation's end (`stepDeep7`).
 *
 * **Its catch** is a harvest rate, the assessment's in its last year: C_2023 / B_2023, the 427.8 thousand lb taken in
 * fishing year 2023 ("Total catch", reported and unreported, Table 7, p. 79) over the 11.22 million lb at its start
 * (Table 15, p. 90), 0.0381 (Table 15's H, 0.04, rounded); a year's catch is that share of the year's starting biomass.
 * Table 15's biomass is the posterior mean ("Estimates of mean exploitable biomass (B)"), Table 12's K its median:
 * their ratio, 56.1% of K in 2023, is not the assessment's own, its medians' product near 58% (B/BMSST 2.04 × BMSST/BMSY
 * 5.67/6.539 × BMSY/K 0.328, Tables 12 and 15, M192's reviewer; a product of medians, not a median: open). At its
 * harvest rate the stock settles at 66.6% of K (the surplus equal to the catch), above that 56.1% of 2023: the assessment's own
 * dynamics at today's fishing, not its projections, which take catch limits (Table 16; open).
 *
 * **Maui Nui's share** (`DEEP7_MAUI_NUI_SHARE`) of the main islands' stock is its share of their hard bottom at 100 to
 * 400 m, "the Maui numbers include Penguin Bank, Molokai, Kahoolawe, and Lanai" (p. 2): 341.8 of 1,060.6 km² (Parke
 * 2007, NOAA Technical Memorandum NMFS-PIFSC-11, Table 1, p. 5; public domain), its biomass and K that share of the
 * assessment's, the same proportion of K and so the same dynamics. ESTIMATED: the fish taken as dense on every km² of
 * hard bottom (Parke's "suitable habitat", hard and steeper than 20°, would give Maui Nui 18.1%, its Penguin Bank
 * gentle; open). The assessment's fishing rate is the archipelago's, taken as Maui Nui's (open).
 *
 * **Its species** (`DEEP7_SPECIES`) are its shares of NOAA's 2024 fishery-independent survey (BFISH), each one's
 * exploited-phase biomass, "≥ 29 cm FL", over the complex's 3,670,558 kg (Richards 2025, *Annual Report: 2024
 * Bottomfish Fishery-Independent Survey in Hawaiʻi*, NOAA Technical Memorandum NMFS-PIFSC-177, Table 4, p. 12; CC0),
 * held fixed (the complex is assessed as one; each species' own dynamics unread, open). The 2024 survey's effort was
 * "unusually low" (p. 13), its species' coefficients of variation 29.8% to 70.9%.
 */

/**
 * The Deep 7's species, each its share of the 2024 survey's exploited-phase biomass, its fish (`surveyFish`) and that
 * survey's coefficient of variation (Richards 2025, Table 4, p. 12); M215: its weight for its length and its depths.
 *
 * - `lw`: weight, kg, for fork length, cm, W = a·L^b, "fitted to paired weight-length observations collected in the MHI
 *   by scientists at NOAA's PIFSC" (Ault et al. 2018, *Fisheries Research* 208: 321–328, Table 4: "Units for L in cm
 *   and W in kilograms"; facts only, its record an accepted manuscript). The same figures are printed in its NOAA
 *   Technical Memorandum, NMFS-PIFSC-67 (public domain), whose caption gives "pounds for weight": read as kilograms,
 *   they give the 2024 survey's own mean weights within 10% (its biomass over its fish, 1.60 kg an ʻōpakapaka against
 *   45 cm's 1.6 kg), as pounds 2.2 times too light (M215's reader).
 * - `depthM`: where most are, m: the ʻōpakapaka's, the kalekale's, the ehu's and the onaga's where NOAA's bottom
 *   cameras counted most, "MaxNo was highest from 90 to 210 m for Opakapaka ... The preferred depths of Kalekale were
 *   180–270 m, and both Onaga and Ehu had the deepest range among species at 210–300 m" (Misa, Drazen, Kelley and
 *   Moriwake 2013, *Fishery Bulletin* 111: 293–308, p. 298, measured, 625 drops at 100 to 300 m); the lehi's, the
 *   gindai's and the hāpuʻupuʻu's their catches' group's, "80-240", "Approximate" (Ralston and Polovina 1982, *Fishery
 *   Bulletin* 80: 435–448, Table 3, p. 440, compiled).
 */
export const DEEP7_SPECIES = [
  // Each named as the assessment's list names it, its local name and its binomial (Syslo et al. 2024, Table 1, p. 62).
  // ʻŌpakapaka, *Pristipomoides filamentosus*.
  { id: 'opakapaka', surveyKg: 2_138_619, surveyFish: 1_338_316, cv: 0.4578, lw: { a: 2.311e-5, b: 2.928 }, depthM: [90, 210] },
  // Kalekale, *Pristipomoides sieboldii*.
  { id: 'kalekale', surveyKg: 717_355, surveyFish: 894_418, cv: 0.5639, lw: { a: 2.243e-5, b: 2.932 }, depthM: [180, 270] },
  // Ehu, *Etelis carbunculus*.
  { id: 'ehu', surveyKg: 540_953, surveyFish: 470_786, cv: 0.2981, lw: { a: 1.551e-5, b: 3.026 }, depthM: [210, 300] },
  // Onaga, *Etelis coruscans*.
  { id: 'onaga', surveyKg: 128_316, surveyFish: 41_777, cv: 0.5822, lw: { a: 6.005e-5, b: 2.673 }, depthM: [210, 300] },
  // Lehi, *Aphareus rutilans*.
  { id: 'lehi', surveyKg: 93_074, surveyFish: 21_266, cv: 0.709, lw: { a: 1.298e-4, b: 2.458 }, depthM: [80, 240] },
  // Gindai, *Pristipomoides zonatus*.
  { id: 'gindai', surveyKg: 28_575, surveyFish: 18_884, cv: 0.4789, lw: { a: 3.526e-5, b: 2.859 }, depthM: [80, 240] },
  // Hāpuʻupuʻu, *Hyporthodus quernus*.
  { id: 'hapuupuu', surveyKg: 23_667, surveyFish: 7_124, cv: 0.5691, lw: { a: 3.065e-5, b: 2.884 }, depthM: [80, 240] },
] as const;

export type Deep7SpeciesId = (typeof DEEP7_SPECIES)[number]['id'];

/** A species' share of the complex's biomass (its survey biomass over the seven's). */
export function deep7Share(id: Deep7SpeciesId): number {
  const all = DEEP7_SPECIES.reduce((a, s) => a + s.surveyKg, 0);
  return (DEEP7_SPECIES.find((s) => s.id === id) as (typeof DEEP7_SPECIES)[number]).surveyKg / all;
}

/** Maui Nui's hard bottom at 100 to 400 m, km², and its share of the main islands' (Parke 2007, Table 1, p. 5; above). */
export const DEEP7_MAUI_NUI_HARD_KM2 = 341.8;
export const DEEP7_MAUI_NUI_SHARE = DEEP7_MAUI_NUI_HARD_KM2 / 1060.6;

/**
 * A species' fish a hectare of Maui Nui's hard bottom at 100 to 400 m on a day (M215): the day's biomass, spread over
 * that bottom as the stock's share is (`DEEP7_MAUI_NUI_SHARE`), times the species' fish a kg of the complex's 2024 survey
 * (its fish over the seven's biomass: its share of the biomass over its own mean weight), the exploited phase's, 29 cm
 * and longer. On a new world's first day, 49.9 kg a hectare and 38.0 fish, 18.2 of them ʻōpakapaka (MEASURED, seed 1).
 */
export function deep7FishPerHa(state: Deep7State, epochDay: number, id: Deep7SpeciesId): number {
  const all = DEEP7_SPECIES.reduce((a, s) => a + s.surveyKg, 0);
  const s = DEEP7_SPECIES.find((x) => x.id === id) as (typeof DEEP7_SPECIES)[number];
  return (deep7BiomassKg(state, epochDay) * s.surveyFish) / all / (DEEP7_MAUI_NUI_HARD_KM2 * 100);
}

/** A species' mean weight in the 2024 survey, kg (its biomass over its fish), and the fork length that weighs it, cm (`lw`). */
export function deep7MeanSize(id: Deep7SpeciesId): { kg: number; forkCm: number } {
  const s = DEEP7_SPECIES.find((x) => x.id === id) as (typeof DEEP7_SPECIES)[number];
  const kg = s.surveyKg / s.surveyFish;
  return { kg, forkCm: (kg / s.lw.a) ** (1 / s.lw.b) };
}

/** The assessment's figures (Syslo et al. 2024; above), Maui Nui's share of its biomass. */
export const DEEP7 = {
  /** Intrinsic growth rate a year, carrying capacity (kg, Maui Nui's share) and the production's shape (Table 12, p. 85). */
  r: 0.09,
  kKg: 20.01e6 * KG_PER_POUND * DEEP7_MAUI_NUI_SHARE,
  m: 0.8,
  /** Process error's variance of the log, a year (Table 12, p. 85). */
  sigma2: 0.005,
  /** The exploitable biomass at the start of fishing year 2023, kg, Maui Nui's share (Table 15's posterior mean, p. 90; above), and that year's start. */
  b2023Kg: 11.22e6 * KG_PER_POUND * DEEP7_MAUI_NUI_SHARE,
  from2023: [2023, 7, 1] as const,
  /** The harvest rate a year: fishing year 2023's total catch over its starting biomass (Tables 7 and 15, pp. 79 and 90). */
  harvest: 427.8e3 / 11.22e6,
  /**
   * The survey's catchability: BFISH's model-based biomass over the assessment's, "qsurv", its posterior median
   * (Table 12, p. 85), so the survey sees about half the exploitable biomass (2023's two tables give 0.43, 4.804 of
   * 11.22 million lb, Tables 11 and 15, a model-based index over a posterior mean: the game's survey of 2023 would
   * read 5.58 million lb; open).
   */
  surveyQ: 0.497,
} as const;

/** The first day of the fishing year a day falls in (1 July). */
export function fishingYearStart(epochDay: number): number {
  const c = civilFromEpochDay(epochDay);
  return epochDayFromCivil(c.month >= 7 ? c.year : c.year - 1, 7, 1);
}

/** A fishing year's change from its starting biomass: the surplus the production gives, less the year's catch. */
export function deep7YearChange(startKg: number): number {
  const { r, kKg, m, harvest } = DEEP7;
  return (r / (m - 1)) * startKg * (1 - (startKg / kKg) ** (m - 1)) - harvest * startKg;
}

/** The biomass on a day, kg: its fishing year's start and that year's change spread over the year's days alike. */
export function deep7BiomassKg(state: Deep7State, epochDay: number): number {
  const start = fishingYearStart(epochDay);
  const days = epochDayFromCivil(civilFromEpochDay(start).year + 1, 7, 1) - start;
  return state.yearStartKg + deep7YearChange(state.yearStartKg) * ((epochDay - start) / days);
}

/**
 * A day of the Deep 7: on 1 July, the year turns, its start the last year's end with the year's process error
 * (`normal` a standard normal draw, the caller's: the game's stream, or 0 at expected values); on any other day,
 * nothing to store (`deep7BiomassKg` reads the day's biomass).
 */
export function stepDeep7(state: Deep7State, epochDay: number, normal: () => number): void {
  if (fishingYearStart(epochDay) !== epochDay) return;
  const end = state.yearStartKg + deep7YearChange(state.yearStartKg);
  state.yearStartKg = held(end * Math.exp(Math.sqrt(DEEP7.sigma2) * normal()));
}
